import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import "./LeaveDetails.css";

const LeaveDetails = () => {
  const [leaveDetails, setLeaveDetails] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [employeeInfo, setEmployeeInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const localUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, []);

  const employeeId = localUser?.id;
  const [filters, setFilters] = useState({
    LeaveTypeID: "",
    FromDate: "",
    ToDate: ""
  });

  useEffect(() => {
    const fetchPageData = async () => {
      if (!employeeId) {
        setError("Unauthorized access Or Session Timed out.");
        return;
      }

      try {
        const [employeeRes, leaveTypeRes] = await Promise.all([
          axios.get("/api/employees/employee-basic", { params: { EmployeeID: employeeId } }),
          axios.get("/api/employees/leave-types-all")
        ]);

        setEmployeeInfo(employeeRes?.data?.data || null);
        setLeaveTypes(Array.isArray(leaveTypeRes?.data?.data) ? leaveTypeRes.data.data : []);
      } catch (e) {
        setError("Failed to load leave filters.");
      }
    };

    fetchPageData();
  }, [employeeId]);

  const formatDate = (value) => {
    if (!value) {
      return "";
    }

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return "";
    }

    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = String(d.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const getDurationLabel = (hfDay) => {
    if (hfDay === "F") {
      return "First Half";
    }

    if (hfDay === "S") {
      return "Second Half";
    }

    return "Full Day";
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const onView = async () => {
    setError("");

    if (!filters.LeaveTypeID || !filters.FromDate || !filters.ToDate) {
      setError("Please select Leave Type, From Date and To Date.");
      return;
    }

    if (new Date(filters.FromDate) > new Date(filters.ToDate)) {
      setError("Invalid date range.");
      return;
    }

    try {
      setLoading(true);
      const response = await axios.get("/api/employees/leave-details", {
        params: {
          EmployeeID: employeeId,
          LeaveTypeID: filters.LeaveTypeID,
          FromDate: filters.FromDate,
          ToDate: filters.ToDate
        }
      });

      setLeaveDetails(Array.isArray(response?.data?.data) ? response.data.data : []);
    } catch (e) {
      setError("Failed to fetch leave details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="leave-details-wrap">
      <div className="leave-top-links">
        <Link to="/new-emp-leave" className="btn btn-primary btn-sm">Apply Leave</Link>
        <Link to="/leave-status" className="btn btn-primary btn-sm">Leave Status</Link>
      </div>
      <h3 className="leave-page-title">Leave Type Wise (From Date - To Date)</h3>

      {error && <div className="alert alert-danger">{error}</div>}

      <div className="leave-filter-grid">
        <div>
          <label className="form-label" htmlFor="EmployeeID">Employee</label>
          <select className="form-control" name="EmployeeID" id="EmployeeID" value={employeeInfo?.EmployeeID || ""} disabled>
            {employeeInfo && (
              <option value={employeeInfo.EmployeeID}>
                {`${employeeInfo.EmpName} - ${employeeInfo.Designation}`}
              </option>
            )}
          </select>
        </div>

        <div>
          <label className="form-label" htmlFor="LeaveTypeID">Leave Type</label>
          <select
            className="form-control"
            name="LeaveTypeID"
            id="LeaveTypeID"
            value={filters.LeaveTypeID}
            onChange={onFilterChange}
          >
            <option value="">Select Leave Type</option>
            {leaveTypes.map(item => (
              <option key={item.LeaveTypeID} value={item.LeaveTypeID}>{item.LeaveType}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="form-label" htmlFor="FromDate">From Date</label>
          <input
            className="form-control"
            type="date"
            id="FromDate"
            name="FromDate"
            value={filters.FromDate}
            onChange={onFilterChange}
          />
        </div>

        <div>
          <label className="form-label" htmlFor="ToDate">To Date</label>
          <input
            className="form-control"
            type="date"
            id="ToDate"
            name="ToDate"
            value={filters.ToDate}
            onChange={onFilterChange}
          />
        </div>

        <div className="leave-view-btn-wrap">
          <label className="form-label">View</label>
          <button type="button" className="btn btn-primary w-100" onClick={onView} disabled={loading}>
            {loading ? "Loading..." : "View"}
          </button>
        </div>
      </div>

      <div id="RptIndvEmpLeave" className="leave-results-wrap">
        <table border="1">
          <thead style={{ '--bs-table-bg': '#008080', color: 'white' }}>
            <tr>
              <th>SNo</th>
              <th>LeaveType</th>
              <th>FromDate</th>
              <th>ToDate</th>
              <th>Days</th>
              <th>Duration</th>
              <th>Remarks</th>
              <th>Status</th>
              <th>LeaveID</th>
              <th>Print</th>
              <th>Attachment</th>
              <th>Progress</th>
            </tr>
          </thead>
          <tbody>
            {leaveDetails.map((leave, index) => (
              <tr key={leave.LeaveID}>
                <td>{index + 1}</td>
                <td>{leave.LeaveType}</td>
                <td>{formatDate(leave.FromDate)}</td>
                <td>{formatDate(leave.ToDate)}</td>
                <td>{leave.NoOfDays}</td>
                <td>{getDurationLabel(leave.HFDay)}</td>
                <td>{leave.Remarks}</td>
                <td>{leave.Status}</td>
                <td>{leave.LeaveID}</td>
                <td><button type="button">Print</button></td>
                <td>
                  <button
                    type="button"
                    onClick={() => {
                      if (leave.SupportingDoc) {
                        window.open(leave.SupportingDoc, "_blank", "noopener,noreferrer");
                      }
                    }}
                  >
                    Attachment
                  </button>
                </td>
                <td><button type="button">Progress</button></td>
              </tr>
            ))}
            {!loading && leaveDetails.length === 0 && (
              <tr>
                <td colSpan="12" className="leave-empty-row">No records found.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LeaveDetails;